import React, {useRef, useCallback, useEffect, useMemo, useState} from 'react';
import Topbar from "../layout/Topbar";
import Sidebar from "../layout/Sidebar";
import {connect} from "react-redux";
import CustomSnackbar from "../alert/CustomSnackbar";
import DataGrid, {SelectColumn} from "react-data-grid";
import {CustomTableHeaderCellRender, IsAtBottom} from "../reporting-table/ReactDataGridUtils";
import {debounce} from "lodash";
import Spinner from "../spinner/Spinner";
import {TrafficSourceConfirmationDialog} from "./TrafficSourceDesign";
import CustomToolbar from "../utils/CustomToolbar";
import {reportingGridStyles} from "../utils/ReportingToolbarDesign";
import {DynamicGridColumnCreator, MarkColumn} from "../reporting-table/Columns";
import {trafficSourceActions} from "../../store/actions/trafficSourceAction";
import {columnActions} from "../../store/actions/columnAction";
import {workspaceActions} from "../../store/actions/workspaceAction";
import {GetCommaSeparatedList} from "../../helpers/common";
import {CssBaseline, Divider, useTheme} from "@mui/material";
import useCustomStyles from "../utils/UseCustomStyles";
import {CustomDataGridBox, CustomMainRootBox} from "../dashboard/Dashboard";
import {CustomTableContainer} from "../logs/LogClicksTable";

import ReportEmptyState from "../reporting-table/ReportEmptyState";

const customStyles = (theme) => ({
    dataGrid: theme.defaultDataGrid
});

function TrafficSourcesGrid(props) {

    const columnGroup = "trafficSources"
    const report = "trafficSource"
    const groupBy = "traffic_source_id";
    const theme = useTheme();
    const classes = useCustomStyles(customStyles, theme);
    const {
        alert, getReport, updateSortBy, updateOrderBy, updateSortColumns, loadMoreRows, getWorkspaceIDs
    } = props;
    const {data, isLoading, errorMsg, name, tags, orderBy, sortBy, sortColumns, rerender} = props.trafficSourceState;
    const {from, to, isDeleted} = props.ddToolbarState;
    const {timezone, currencyCode} = props.auth.userSettings;

    const workspaceIDs = (props?.workspace?.userWorkspaces || [])
        .filter(workspace => workspace?.selected && workspace?.workspaceId)
        .map(workspace => workspace.workspaceId).join(",");
    const workspacesReady = workspaceIDs !== "";

    const gridRef = useRef(null);
    const lastScrollTop = useRef(0);

    const [selectedRows, setSelectedRows] = useState(() => new Set());
    const [columns, setColumns] = useState(null);
    const columnsReady = columns !== null;

    const updateColumnResize = (idx, width) => {
        props.updateColumnWidth({columnKey: columns[idx]?.key, width, columnGroup, report});
    };

    const modifiedTableColumns = useMemo(() => {
        function CustomHeaderCell(props) {
            return <CustomTableHeaderCellRender {...props}/>;
        }

        return columns?.map((c) => {
            if (c?.key === "select-row" || c?.key === "mark") {
                return c;
            }
            return {...c, renderHeaderCell: CustomHeaderCell};
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, columnGroup, report]);

    const getTableColumns = () => {
        let columns = [];
        props?.column?.data[columnGroup]?.forEach((col) => {
            if (!col?.hidden) {
                columns.push(col?.reportColumnKey);
            }
        });
        return columns;
    };

    const handleGetReportData = ({exportCSV = "", pageSize = 100}) => {
        if (!workspacesReady) {
            return;
        }
        lastScrollTop.current = 0;
        selectedRows.clear(); // Clear all selected values
        gridRef?.current?.element?.scrollTo(0, 0);
        getReport(groupBy, from, to, timezone, name, tags, isDeleted, 1, pageSize, orderBy, sortBy, currencyCode, exportCSV, props?.metricFilter.filters, getTableColumns(), getWorkspaceIDs());
    };

    const handleInfinityScroll = (event) => {
        const {scrollTop} = event.currentTarget;
        const scrollingDown = scrollTop > lastScrollTop.current;
        lastScrollTop.current = scrollTop;

        if (!scrollingDown || isLoading || errorMsg || !workspacesReady || !IsAtBottom(event)) {
            return;
        }

        const hasMoreRows = data?.page > 0 && data?.recordsPerPage > 0 &&
            data.page < Math.ceil(data?.total?.totalRecords / data.recordsPerPage);
        if (hasMoreRows) {
            selectedRows.clear(); // Clear all selected values
            loadMoreRows(groupBy, from, to, timezone, name, tags, isDeleted, data.page + 1, 100, orderBy, sortBy, currencyCode, props?.metricFilter.filters, getTableColumns(), getWorkspaceIDs());
        }
    };

    const handleRefresh = () => {
        if (!workspacesReady || isLoading) {
            return;
        }
        lastScrollTop.current = 0;
        gridRef?.current?.element?.scrollTo(0, 0);
        handleGetReportData({});
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleColumnResize = useCallback(debounce(updateColumnResize, 500), [columns, columnGroup, report]);

    const handleColumnRender = () => {
        let columns = [
            SelectColumn,
            MarkColumn,
            ...DynamicGridColumnCreator({columns: props?.column?.data[columnGroup]})
        ];
        setColumns(columns);
    };

    // 1. Generate grid columns dynamically at once.
    useEffect(() => {
        if (props?.column?.data !== null && columns === null) {
            handleColumnRender();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.column?.data]);

    // 2. Get report data.
    useEffect(() => {
        if (columns !== null) {
            handleGetReportData({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsReady, workspacesReady, workspaceIDs, from, to, timezone, name, tags, isDeleted, sortBy, orderBy, currencyCode, props?.metricFilter?.filters]);

    // 3. Rerender
    useEffect(() => {
        if (rerender && columns !== null) {
            handleGetReportData({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerender]);

    useEffect(() => {
        if (columns !== null) {
            handleColumnRender();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.column?.data]);

    useEffect(() => {
        if (sortColumns.length === 0) {
            updateSortBy("");
            updateOrderBy("");
            return () => {
            };
        }
        const {columnKey, direction} = sortColumns[0];
        updateOrderBy(columnKey);
        updateSortBy(direction);
    }, [updateOrderBy, updateSortBy, sortColumns]);

    // 6. Rerender if user workspaces has changed.
    useEffect(() => {
        if (props?.workspace?.updated !== null) {
            props?.resetUpdatedWorkspaceValue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.workspace?.updated])

    return (
        <CustomMainRootBox>
            <CssBaseline/>
            <Sidebar {...props}/>
            <Topbar/>
            <CustomTableContainer>
                <CustomToolbar
                    confirmationDialog={TrafficSourceConfirmationDialog}
                    columns={columns}
                    selectedRows={selectedRows}
                    onChangeToPage={props.rerender}
                    searchTerm={name}
                    onChangeSearch={props.updateName}
                    tagsTerm={tags}
                    onChangeTags={props.updateTags}
                    onClickAdd={() => {
                        props.history.push({
                            pathname: "/traffic-sources/new",
                            state: {background: props.location}
                        });
                    }}
                    onClickRefresh={handleRefresh}
                    onClickExportCSV={() => handleGetReportData({exportCSV: "csv", pageSize: 10000})}
                    onClickEdit={(selectedRowId) => {
                        props.history.push({
                            pathname: "/traffic-sources/edit/" + selectedRowId,
                            state: {background: props.location}
                        });
                    }}
                    onClickDuplicate={(selectedRowId) => {
                        props.history.push({
                            pathname: "/traffic-sources/duplicate/" + selectedRowId,
                            state: {background: props.location}
                        });
                    }}
                    onClickArchive={(selectedRowsIds) => {
                        selectedRows.clear();
                        props.archive({ids: selectedRowsIds})
                    }}
                    onClickRestore={(selectedRowsIds) => {
                        selectedRows.clear();
                        props.restore({ids: selectedRowsIds})
                    }}
                    onSubmitChangeWorkspace={(data) => {
                        data.trafficIds = '' + GetCommaSeparatedList(selectedRows);
                        props?.changeWorkspace(data);
                    }}
                    report={report}
                    columnGroup={columnGroup}
                />
                <Divider/>
                <CustomDataGridBox sx={reportingGridStyles}>

                    {alert.errorMsg !== "" && <CustomSnackbar message={alert.errorMsg}/>}
                    {isLoading && <Spinner overComponentBox={true}/>}

                    {columns !== null && classes !== null &&
                        <DataGrid
                            ref={gridRef}
                            rowKeyGetter={(row) => row.id}
                            columns={modifiedTableColumns}
                            rows={data?.records ? data.records : []}
                            defaultColumnOptions={{
                                sortable: true,
                                resizable: true,
                                draggable: false
                            }}
                            onColumnResize={handleColumnResize}
                            sortColumns={sortColumns}
                            onSortColumnsChange={updateSortColumns}
                            selectedRows={selectedRows}
                            onSelectedRowsChange={setSelectedRows}
                            onSelectedCellChange={({rowIdx, row, column}) => {
                                if (rowIdx !== -1 && column?.key !== "select-row") {
                                    setSelectedRows(new Set().add(row.id));
                                }
                            }}
                            onScroll={handleInfinityScroll}
                            bottomSummaryRows={[data]}
                            enableVirtualization={true}
                            className={classes?.dataGrid}
                        />
                    }
                    {workspacesReady && !isLoading && !errorMsg && !alert.errorMsg && columns !== null && classes !== null &&
                        data?.page > 0 && !data?.records?.length && <ReportEmptyState kind="trafficSources" archived={isDeleted}/>}
                </CustomDataGridBox>
            </CustomTableContainer>
        </CustomMainRootBox>
    );
}

const mapStateToProps = (state) => {
    return {
        trafficSourceState: state.trafficSource,
        ddToolbarState: state.ddToolbar,
        auth: state.auth,
        alert: state.alert,
        metricFilter: state.metricFilter,
        column: state.column,
        workspace: state.workspace
    }
};

const actionCreators = {
    getReport: trafficSourceActions.getReport,
    loadMoreRows: trafficSourceActions.loadMoreRows,
    updateOrderBy: trafficSourceActions.updateOrderBy,
    updateSortBy: trafficSourceActions.updateSortBy,
    updateName: trafficSourceActions.updateName,
    updateTags: trafficSourceActions.updateTags,
    updateSortColumns: trafficSourceActions.updateSortColumns,
    changeWorkspace: trafficSourceActions.changeWorkspace,
    archive: trafficSourceActions.archive,
    restore: trafficSourceActions.restore,
    updateColumnWidth: columnActions.updateColumnWidth,
    updateColumnSequence: columnActions.updateColumnSequence,
    getWorkspaceIDs: workspaceActions.getWorkspaceIDs,
    resetUpdatedWorkspaceValue: workspaceActions.resetUpdatedWorkspaceValue
};

export default connect(mapStateToProps, actionCreators)(TrafficSourcesGrid);